'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';

import { userPoolEligibilityBunja } from '@flipster/features/src/userpool/user-pool.state';
import { useVipTrialReferrer } from '@flipster/features/src/vip/use-vip-trial-referrer';
import { Carousel } from '@flipster/react/design-system-legacy/carousel';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { Picture } from '@flipster/react/design-utils/picture';
import { css } from '@flipster/styled-system/css';
import { linkOverlay } from '@flipster/styled-system/patterns';
import { breakpointMB } from '@flipster/utils/constants/breakpoint';
import { isTradingFeeFeatureEnabled } from '@flipster/utils/feature-flag';
// vip trial 시범 운영기간용 배너 이미지
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';
import { VipTrialEvent } from '@flipster/utils/segment/events/vip';
import { ProdStaticPath } from '@flipster/utils/url';

import { maxWStyles, sectionSmallHeadingStyles } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';
import { BannerItemFragment } from '@web/app/[locale]/(web)/_main-page/query.generated';
import vipTrialBannerDesktop from '@web/assets/images/main-page/vip-trial-banner-desktop.webp';
import vipTrialBannerMobile from '@web/assets/images/main-page/vip-trial-banner-mobile.webp';

interface Props {
  promotions?: BannerItemFragment[];
}

const Promotions = ({ promotions }: Props) => {
  const { data: vipTrialReferrer } = useVipTrialReferrer();
  const intersectionRef = useRef<HTMLHeadingElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Promotions');

  const { userPoolEligibilityAtom } = useBunja(userPoolEligibilityBunja);
  const userPoolEligibility = useAtomValue(userPoolEligibilityAtom);

  const hasUserPoolEligibility = userPoolEligibility?.eligible;

  if (!promotions?.length && !vipTrialReferrer?.canInvite && !hasUserPoolEligibility) return null;
  return (
    <Carousel
      spaceBetween={8}
      breakpoints={{ [breakpointMB]: { spaceBetween: 16 } }}
      slidesPerView='auto'
      withOverflow
      css={{ ...maxWStyles, py: { base: 48, tablet: 64 } }}
      title={
        <h2 ref={intersectionRef} className={css(sectionSmallHeadingStyles)}>
          Promotions
        </h2>
      }
      navigator={<Carousel.Navigator css={{ tabletDown: { display: 'none' } }} />}
    >
      {hasUserPoolEligibility && (
        <Carousel.Slide css={itemStyles}>
          <IsomorphicLink
            href={`/userpool/${userPoolEligibility?.id}`}
            className={linkOverlay()}
            tabIndex={-1}
            onClick={() => {
              segment.track(MainEvent.MainPagePromotionCarouselBannerClick, {
                buttun_url: '/userpool',
                image_name: 'new_joiner_pool_banner',
              });
            }}
          />
          <Picture
            src={{
              base: `${ProdStaticPath}/images/banner/banner-promotion-mobile.webp`,
              tablet: `${ProdStaticPath}/images/banner/banner-promotion-desktop.webp`,
            }}
            width={512}
            height={200}
            alt='New joiner pool'
          />
        </Carousel.Slide>
      )}
      {/* vip trial 시범 운영기간용 하드코딩 캐러셀 아이템 */}
      {!isTradingFeeFeatureEnabled && vipTrialReferrer?.canInvite && (
        <Carousel.Slide css={itemStyles}>
          <IsomorphicLink
            href='/vip-trial/referrer'
            onClick={() => {
              segment.track(VipTrialEvent.VipTrialBannerClick, {
                image_name: 'vip_trial_banner',
                event_type: 'vip_trial',
              });
            }}
            className={linkOverlay()}
            tabIndex={-1}
          />
          <Picture
            src={{
              base: vipTrialBannerMobile.src,
              tablet: vipTrialBannerDesktop.src,
            }}
            width={512}
            height={200}
            alt='VIP Trial'
          />
        </Carousel.Slide>
      )}
      {promotions?.map((b, index) => {
        return (
          <Carousel.Slide key={`item-${b.alt}-${index}`} css={itemStyles}>
            <IsomorphicLink
              href={b.url || ''}
              className={linkOverlay()}
              tabIndex={-1}
              external
              onClick={() => {
                segment.track(MainEvent.MainPagePromotionCarouselBannerClick, {
                  buttun_url: b.url || '',
                  image_name: b.alt || 'promotion image',
                });
              }}
            />
            <Picture
              src={{
                base: b.mobileImage?.url + '?fm=webp&q=60',
                tablet: b.desktopImage?.url + '?fm=webp&q=60',
              }}
              width={512}
              height={200}
              alt={b.alt || 'promotion image'}
            />
          </Carousel.Slide>
        );
      })}
    </Carousel>
  );
};

export default Promotions;

const itemStyles = css.raw({
  pos: 'relative',
  overflow: 'hidden',
  rounded: { base: 8, tablet: 16 },
  w: { base: 292, tablet: 512 },
  h: { base: 134, tablet: 200 },
  mr: { base: 8, tablet: 16 },
  '& img': {
    w: '100%',
    h: 'auto',
  },
});
